/* eslint-disable lingui/text-restrictions */
/* eslint-disable lingui/no-unlocalized-strings */

import { t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import {
  Chats,
  CircleNotch,
  Clock,
  CreditCard,
  Envelope,
  Lightbulb,
  PaperPlaneTilt,
  Question,
  Wrench,
} from "@phosphor-icons/react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@reactive-resume/ui";
import { motion } from "framer-motion";
import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";

import { toast } from "@/client/hooks/use-toast";
import { axios } from "@/client/libs/axios";

const TOPICS = [
  { value: "general", label: "General Question", icon: Question },
  { value: "billing", label: "Billing & Plans", icon: CreditCard },
  { value: "support", label: "Technical Support", icon: Wrench },
  { value: "feedback", label: "Feature Feedback", icon: Lightbulb },
  { value: "partnership", label: "Partnership Inquiry", icon: Chats },
  { value: "other", label: "Other", icon: Envelope },
] as const;

const CONTACT_FAQS = [
  {
    q: "How quickly will I get a response?",
    a: "We aim to respond to all inquiries within 24 hours on business days. Account access and technical support issues are prioritized.",
  },
  {
    q: "I'm having trouble logging in or accessing my account.",
    a: "First, try resetting your password using the \"Forgot Password\" link on the login page. If you're still having trouble, reach out through the form above with the email address associated with your account, and we'll help you regain access.",
  },
  {
    q: "How do I cancel my subscription?",
    a: "Open Settings in your dashboard and use Manage Billing to open the Stripe portal. You can cancel anytime. Access continues until the end of the current period. If you need help, select \"Account & billing\" in the form above.",
  },
  {
    q: "I have a feature idea or integration request.",
    a: "We love hearing from users! Select \"Feature Feedback\" in the contact form and describe your idea in detail. We review every suggestion and prioritize based on community demand. Many of our recent features started as user suggestions.",
  },
  {
    q: "Is there a way to get in touch faster for urgent issues?",
    a: "For account-access issues, include \"URGENT\" at the start of your message and we'll prioritize it.",
  },
];

const WAYS_TO_REACH = [
  {
    icon: Envelope,
    title: "Email Us",
    description: "Send us an email directly",
    detail: "hello@careerbldr.com",
    href: "mailto:hello@careerbldr.com",
  },
  {
    icon: Clock,
    title: "Response Time",
    description: "We typically respond within",
    detail: "24 hours on business days",
  },
  {
    icon: CreditCard,
    title: "Billing Help",
    description: "Manage your plan via",
    detail: "Dashboard → Settings → Billing",
    href: "/dashboard/settings",
    internal: true,
  },
];

type FormState = {
  name: string;
  email: string;
  topic: string;
  message: string;
};

export const ContactPage = () => {
  const { i18n } = useLingui();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState<FormState>({
    name: "",
    email: "",
    topic: "",
    message: "",
  });

  const isValid = form.name.trim() && form.email.trim() && form.topic && form.message.trim().length >= 10;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid || loading) return;

    setLoading(true);
    try {
      await axios.post("/contact", {
        name: form.name.trim(),
        email: form.email.trim(),
        topic: form.topic,
        message: form.message.trim(),
      });

      setSubmitted(true);
      toast({
        variant: "success",
        title: t`Message sent!`,
        description: t`We'll get back to you as soon as possible.`,
      });
    } catch {
      toast({
        variant: "error",
        title: t`Something went wrong`,
        description: t`Please try again or email us directly at hello@careerbldr.com`,
      });
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <main className="relative bg-background">
      <Helmet prioritizeSeoTags>
        <html lang={i18n.locale} />
        <title>
          {t`Contact Us`} - {t`CareerBldr`}
        </title>
        <meta
          name="description"
          content="Get in touch with the CareerBldr team. Ask questions about features, billing, get technical support, share feedback, or explore partnership opportunities."
        />
      </Helmet>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border/30 bg-secondary-accent/30">
        <div className="container pb-16 pt-20 sm:pb-20 sm:pt-28">
          <motion.div
            className="mx-auto max-w-2xl text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0, transition: { duration: 0.4 } }}
          >
            <p className="text-sm font-semibold uppercase tracking-wider text-primary">
              {t`Contact`}
            </p>
            <h1 className="mt-3 text-4xl font-bold tracking-tight sm:text-5xl">
              {t`We'd love to hear from you`}
            </h1>
            <p className="mt-4 text-base leading-relaxed text-foreground/60 sm:text-lg">
              Whether you have a question about features, need help with your account,
              or want to share feedback, our team is ready to help.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Quick contact methods */}
      <section className="container -mt-8 relative z-10">
        <div className="grid gap-4 sm:grid-cols-3">
          {WAYS_TO_REACH.map((item, index) => (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0, transition: { delay: 0.1 + index * 0.05, duration: 0.3 } }}
            >
              {item.href ? (
                item.internal ? (
                  <Link
                    to={item.href}
                    className="flex items-start gap-4 rounded-xl border bg-background p-5 shadow-sm transition-colors hover:border-primary/30 hover:bg-secondary/30"
                  >
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <item.icon size={20} weight="duotone" />
                    </div>
                    <div>
                      <p className="font-medium">{item.title}</p>
                      <p className="mt-0.5 text-sm text-foreground/50">{item.description}</p>
                      <p className="mt-1 text-sm font-medium text-primary">{item.detail}</p>
                    </div>
                  </Link>
                ) : (
                  <a
                    href={item.href}
                    className="flex items-start gap-4 rounded-xl border bg-background p-5 shadow-sm transition-colors hover:border-primary/30 hover:bg-secondary/30"
                  >
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <item.icon size={20} weight="duotone" />
                    </div>
                    <div>
                      <p className="font-medium">{item.title}</p>
                      <p className="mt-0.5 text-sm text-foreground/50">{item.description}</p>
                      <p className="mt-1 text-sm font-medium text-primary">{item.detail}</p>
                    </div>
                  </a>
                )
              ) : (
                <div className="flex items-start gap-4 rounded-xl border bg-background p-5 shadow-sm">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <item.icon size={20} weight="duotone" />
                  </div>
                  <div>
                    <p className="font-medium">{item.title}</p>
                    <p className="mt-0.5 text-sm text-foreground/50">{item.description}</p>
                    <p className="mt-1 text-sm font-medium text-foreground/80">{item.detail}</p>
                  </div>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </section>

      {/* Form + info section */}
      <section className="container py-20 sm:py-28">
        <div className="grid gap-16 lg:grid-cols-5">
          {/* Form */}
          <motion.div
            className="lg:col-span-3"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0, transition: { delay: 0.15, duration: 0.4 } }}
          >
            <h2 className="text-2xl font-bold tracking-tight">{t`Send us a message`}</h2>
            <p className="mt-2 text-sm text-foreground/60">
              Fill out the form below and we'll get back to you as soon as possible.
              All fields are required.
            </p>

            {submitted ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="mt-8 rounded-xl border border-green-500/20 bg-green-500/5 p-8 text-center"
              >
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-green-500/10">
                  <PaperPlaneTilt size={28} weight="duotone" className="text-green-600" />
                </div>
                <h3 className="mt-4 text-xl font-semibold">{t`Message sent!`}</h3>
                <p className="mt-2 text-sm text-foreground/60">
                  Thank you for reaching out. We've received your message and will respond
                  within 24 hours on business days.
                </p>
                <Button
                  variant="outline"
                  className="mt-6"
                  onClick={() => {
                    setSubmitted(false);
                    setForm({ name: "", email: "", topic: "", message: "" });
                  }}
                >
                  {t`Send another message`}
                </Button>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="mt-8 space-y-6">
                <div className="grid gap-6 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="name">{t`Full Name`}</Label>
                    <Input
                      id="name"
                      placeholder="John Doe"
                      value={form.name}
                      onChange={(e) => updateField("name", e.target.value)}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">{t`Email Address`}</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="john@example.com"
                      value={form.email}
                      onChange={(e) => updateField("email", e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="topic">{t`Topic`}</Label>
                  <Select value={form.topic} onValueChange={(val) => updateField("topic", val)}>
                    <SelectTrigger id="topic">
                      <SelectValue placeholder={t`What can we help you with?`} />
                    </SelectTrigger>
                    <SelectContent>
                      {TOPICS.map((topic) => (
                        <SelectItem key={topic.value} value={topic.value}>
                          <span className="flex items-center gap-2">
                            <topic.icon size={14} className="opacity-60" />
                            {topic.label}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message">{t`Message`}</Label>
                  <textarea
                    id="message"
                    rows={6}
                    placeholder="Tell us what's on your mind. The more detail you provide, the better we can help..."
                    value={form.message}
                    onChange={(e) => updateField("message", e.target.value)}
                    className="flex w-full resize-y rounded-sm border border-border bg-transparent px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-foreground/30 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-50"
                    required
                    minLength={10}
                  />
                  <p className="text-xs text-foreground/40">
                    {form.message.length < 10
                      ? `At least 10 characters required (${form.message.length}/10)`
                      : `${form.message.length} characters`}
                  </p>
                </div>

                <Button type="submit" disabled={!isValid || loading} className="w-full sm:w-auto">
                  {loading ? (
                    <CircleNotch size={16} className="animate-spin" />
                  ) : (
                    <>
                      <PaperPlaneTilt size={16} weight="duotone" className="mr-2" />
                      {t`Send Message`}
                    </>
                  )}
                </Button>
              </form>
            )}
          </motion.div>

          {/* Sidebar info */}
          <motion.div
            className="space-y-8 lg:col-span-2"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0, transition: { delay: 0.25, duration: 0.4 } }}
          >
            <div className="rounded-xl border bg-secondary/30 p-6">
              <h3 className="font-semibold">{t`Before you write...`}</h3>
              <ul className="mt-4 space-y-3 text-sm text-foreground/60">
                <li className="flex items-start gap-2">
                  <span className="mt-0.5 text-primary">•</span>
                  <span>
                    <strong className="text-foreground/80">Account issues?</strong>{" "}
                    Try resetting your password from the{" "}
                    <Link to="/auth/forgot-password" className="text-primary underline">
                      login page
                    </Link>{" "}
                    first.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-0.5 text-primary">•</span>
                  <span>
                    <strong className="text-foreground/80">Billing?</strong>{" "}
                    Manage or cancel your plan in{" "}
                    <Link to="/dashboard/settings" className="text-primary underline">
                      Settings
                    </Link>.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-0.5 text-primary">•</span>
                  <span>
                    <strong className="text-foreground/80">Resume help?</strong>{" "}
                    Check our{" "}
                    <a href="/blog/category/resume-writing/" className="text-primary underline">
                      resume tips blog
                    </a>{" "}
                    for writing guides.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-0.5 text-primary">•</span>
                  <span>
                    <strong className="text-foreground/80">Feature info?</strong>{" "}
                    Visit our{" "}
                    <Link to="/#mission" className="text-primary underline">
                      home page
                    </Link>{" "}
                    for a full feature comparison.
                  </span>
                </li>
              </ul>
            </div>

            <div className="rounded-xl border bg-secondary/30 p-6">
              <h3 className="font-semibold">{t`What to include`}</h3>
              <p className="mt-2 text-sm text-foreground/60">
                Help us help you faster by including:
              </p>
              <ul className="mt-3 space-y-2 text-sm text-foreground/60">
                <li className="flex items-start gap-2">
                  <span className="mt-0.5 text-primary">→</span>
                  Your account email address
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-0.5 text-primary">→</span>
                  Steps to reproduce (for bugs)
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-0.5 text-primary">→</span>
                  Browser and device info (if relevant)
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-0.5 text-primary">→</span>
                  Screenshots or error messages
                </li>
              </ul>
            </div>

            <div className="rounded-xl border border-primary/20 bg-primary/5 p-6">
              <h3 className="font-semibold text-primary">{t`Start a 3-day trial`}</h3>
              <p className="mt-2 text-sm text-foreground/60">
                CareerBldr is operated by Studio Algorithm. Plans start at $9/month, with a 3-day
                free trial on new subscriptions.
              </p>
              <Button asChild variant="outline" size="sm" className="mt-4">
                <Link to="/auth/register">Get started</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="border-t border-border/30 bg-secondary-accent/30 py-20 sm:py-28">
        <div className="container">
          <div className="grid gap-12 lg:grid-cols-3">
            <motion.div
              className="space-y-4"
              viewport={{ once: true }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
            >
              <p className="text-sm font-semibold uppercase tracking-wider text-primary">
                {t`Support FAQ`}
              </p>
              <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
                {t`Common Questions`}
              </h2>
              <p className="text-sm leading-relaxed text-foreground/60">
                Quick answers to the questions we hear most often.
                If you don't find what you're looking for, use the form above.
              </p>
            </motion.div>

            <motion.div
              className="col-span-2"
              viewport={{ once: true }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0, transition: { delay: 0.1 } }}
            >
              <Accordion collapsible type="single">
                {CONTACT_FAQS.map((faq, index) => (
                  <AccordionItem key={index} value={String(index)}>
                    <AccordionTrigger className="text-left leading-relaxed">
                      {faq.q}
                    </AccordionTrigger>
                    <AccordionContent className="prose max-w-none text-sm leading-relaxed text-foreground/70 dark:prose-invert">
                      <p>{faq.a}</p>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="container py-16 text-center sm:py-20">
        <motion.div
          className="mx-auto max-w-xl"
          viewport={{ once: true }}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
        >
          <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
            {t`Ready to build your resume?`}
          </h2>
          <p className="mt-3 text-foreground/60">
            Get started with CareerBldr and create an ATS-optimized resume in minutes.
          </p>
          <div className="mt-6 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
            <Button asChild size="lg">
              <Link to="/auth/register">{t`Get Started`}</Link>
            </Button>
            <Button asChild variant="outline" size="lg">
              <Link to="/#mission">{t`Get started`}</Link>
            </Button>
          </div>
        </motion.div>
      </section>
    </main>
  );
};
